import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { IPaciente } from '@app/modules/paciente/models/paciente.model';
import { PacienteService } from '@app/modules/paciente/services/paciente.service';
import { IParceiro, TIPO_SEXO } from '@app/modules/parceiro/model';
import { ParceiroService } from '@app/modules/parceiro/services/parceiro.service';
import {
  ConfirmDialogComponent,
  ConfirmDialogData
} from '@app/shared/components';
import { validateCnpj, validateCpf } from '@app/shared/utils';
import { of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import {
  CustomValidators,
  ErroMessages,
  IErroMessages,
  Pattern,
  TokenService,
  Utils
} from 'viggo-sistemas-core-web';

@Component({
  selector: 'form-info-paciente',
  templateUrl: './form-info-paciente.component.html',
  providers: [ParceiroService, PacienteService]
})
export class FormInfoPacienteComponent implements OnInit {
  form: UntypedFormGroup;

  @Input() entity: any; // IParceiro
  @Input() notRequired = false;
  @Input() requiredFantasia = false;
  @Input() showObs = false;
  @Input() parceiroType = 'parceiro';

  @Output() onLoadParceiro = new EventEmitter();

  @Output() isRequired = new EventEmitter<boolean>();

  findParceiro = false;
  loadingParceiro = false;

  messages = {
    nome_razao_social: {
      required: 'O nome é requerido.',
      maxlength: 'O nome deve ter no maximo 60 caracteres.',
      minlength: 'O nome é requerido.',
      isEmptyValue: 'Valor inválido: remova os espaços em branco!'
    },
    apelido_nome_fantasia: {
      maxlength: 'Este nome é muito longo.',
      minlength: 'Este nome é muito curto.',
      isEmptyValue: 'Valor inválido: remova os espaços em branco!'
    },
    cpf_cnpj: {
      repeated: 'Informação já cadastrada.',
      mask: 'Valor inválido.',
      required: 'O CPF/CNPJ é obrigatório',
      pattern: 'Este CPF/CNPJ é inválido.',
      maxlength: 'O CPF/CNPJ deve conter no máximo 14 caracteres.',
      duplicate: 'O CPF/CNPJ já esta cadastrado',
      validateCPF: 'Este CPF é inválido.',
      validateCNPJ: 'Este CNPJ é inválido.'
    },
    rg_insc_est: {
      pattern: 'Este valor é inválido.',
      maxlength: 'O campo deve conter no máximo 20 caracteres.',
      duplicate: 'Rg/Insc.Est já existe na base de dados',
      existSpecialChar: 'Valor inválido, não aceita pontuação!',
      isEmptyValue: 'Valor inválido: remova os espaços em branco!'
    },
    observacao: {
      isEmptyValue: 'Valor inválido, remova os espaços!',
      maxlength: 'A observação deve conter no máximo 500 caracteres.'
    },
    tipo_sexo: {
      required: 'Sexo é obrigatório'
    },
    data_nascimento: {
      matDatepickerParse: 'Esta data é inválida.',
      invalidDate: 'Data não pode ser posterior a atual',
      isEmptyValue: 'Valor inválido! Remova os espaços em branco.'
    }
  };

  tipoSexo: { label: string; value: string }[];
  focusElement: any;
  inputElementDtNasc: boolean;
  ID_FIELD_DTNASC = 'field-data-nascimento';
  ID_DTPICKER_NASC = 'dtPickerToggleDtNasc';

  constructor(
    public matSnackBar: MatSnackBar,
    public matDialog: MatDialog,
    public router: Router,
    protected fb: UntypedFormBuilder,
    protected pacienteService: PacienteService,
    protected parceiroService: ParceiroService,
    protected tokenService: TokenService
  ) {
    this.tipoSexo = Utils.enumToLisObjet(TIPO_SEXO);
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      nome_razao_social: [
        '',
        [Validators.required, Validators.maxLength(60), Validators.minLength(1)]
      ],
      apelido_nome_fantasia: [
        '',
        [Validators.maxLength(60), Validators.minLength(1)]
      ],
      cpf_cnpj: ['', [Validators.required]],
      rg_insc_est: [
        '',
        [
          Validators.pattern(Pattern.patternInscEstadual()),
          Validators.maxLength(20)
        ]
      ],
      user: [null, [CustomValidators.typeIs('object')]],
      sexo: [this.tipoSexo[0].value, [Validators.required]],
      data_nascimento: [null, [CustomValidators.isEmptyValue()]]
    });
    const {
      cpf_cnpj,
      apelido_nome_fantasia,
      nome_razao_social,
      rg_insc_est
    } = this.form.controls;

    cpf_cnpj.valueChanges.subscribe(vl => {
      if (!vl || vl === '') {
        if (!this.notRequired && this.parceiroType === 'cliente') {
          nome_razao_social.setValue(apelido_nome_fantasia.value);
          apelido_nome_fantasia.reset();
        }
        rg_insc_est.reset();
        this.isRequired.emit(true);
        this.notRequired = true;
      } else {
        if (this.notRequired && this.parceiroType === 'cliente') {
          apelido_nome_fantasia.setValue(nome_razao_social.value);
          // nome_razao_social.reset();
        }
        this.isRequired.emit(false);
        this.notRequired = false;
        this.validar();
      }
    });
    this.markAllControlsAsDirty();
  }

  validar() {
    const cpfCnpjValue = this.form.controls.cpf_cnpj.value;
    if (cpfCnpjValue.toString().length <= 11) {
      if (validateCpf(cpfCnpjValue)) {
        this.form.controls.cpf_cnpj.setErrors({ validateCPF: true });
        this.form.updateValueAndValidity();
      }
    } else {
      if (validateCnpj(cpfCnpjValue)) {
        this.form.controls.cpf_cnpj.setErrors({ validateCNPJ: true });
        this.form.updateValueAndValidity();
      }
    }
  }

  notText(event) {
    if (
      (!/^\d+$/.test(event.key) || /[`,.]/.test(event.key)) &&
      event.key !== 'Backspace' &&
      event.key !== 'Delete' &&
      event.key !== 'Tab' &&
      event.key !== 'ArrowLeft' &&
      event.key !== 'ArrowRight' &&
      !event.ctrlKey
    )
      return false;
  }

  public checkCpfCnpj(): void {
    const cpfCnpj = this.form.controls.cpf_cnpj;

    if (cpfCnpj.invalid || !cpfCnpj.value || cpfCnpj.value === undefined)
      return;
    const cpfCnpjValue: string = cpfCnpj.value;
    this.loadingParceiro = true;

    this.parceiroService
      .getEntitiesByParams(
        `cpf_cnpj=${cpfCnpjValue}`,
        this.tokenService.getDomain().id
      )
      .pipe(
        map(parceiros => {
          if (parceiros.length) {
            this.findParceiro = true;
            return { ...parceiros[0], cpf_cnpj: parceiros[0].cpf_cnpj.trim() };
          } else {
            this.findParceiro = false;
            return { contatos: [], enderecos: [] } as IParceiro;
          }
        }),
        mergeMap(parceiro => {
          if (!parceiro || !parceiro.id || parceiro.id === this.entity.id)
            return of(parceiro);
          return this.pacienteService.getEntity(parceiro.id).pipe(
            map(ent => {
              if (ent && ent.id) {
                this.form.controls.cpf_cnpj.setErrors({ duplicate: true });
                this.form.updateValueAndValidity();
                return { contatos: [], enderecos: [] } as IParceiro;
              }
              return parceiro;
            }),
            catchError(() => of(parceiro))
          );
        })
      )
      .subscribe(
        (parceiro: IParceiro) => {
          this.loadingParceiro = false;
          if (!parceiro.id) return;
          else {
            this.onLoadParceiro.emit(parceiro);
            this.form.patchValue({
              apelido_nome_fantasia: parceiro.apelido_nome_fantasia || null,
              nome_razao_social: parceiro.nome_razao_social || null,
              rg_insc_est: parceiro.rg_insc_est || null,
              user: parceiro.user || null,
              user_id: parceiro.user_id || null,
              data_nascimento: parceiro.data_nascimento || null
            });
          }
        },
        (err: HttpErrorResponse) => {
          this.loadingParceiro = false;
          this.displayError(err);
        }
      );
  }

  public checkRgInscEstadual(): void {
    const rgInsc = this.form.controls.rg_insc_est;

    if (
      rgInsc.invalid ||
      rgInsc.value === null ||
      this.findParceiro ||
      rgInsc.value === '' ||
      rgInsc.value === this.entity.parceiro.rg_insc_est
    )
      return;
    const rgInscValue: string = rgInsc.value.replace(/[^a-zA-Z0-9 ]/g, '');

    this.parceiroService
      .getEntitiesByParams(
        `rg_insc_est=${rgInscValue}`,
        this.tokenService.getDomain().id
      )
      .subscribe(
        (parceiro: IParceiro) => {
          if (parceiro[0] && parceiro[0].id) {
            this.form.controls.rg_insc_est.setErrors({ duplicate: true });
            this.form.updateValueAndValidity();
            return {} as IParceiro;
          }
        },
        (err: HttpErrorResponse) => this.displayError(err)
      );
  }

  /**
   * @description
   * Reune as informações que são relevantes para o paciente
   * diretamente do formulário fazendo um procedimento e retornandoas.
   * @returns {IPaciente} informações da parte de identificação
   */
  getValues() {
    let paciente: IPaciente = { ...this.form.value };
    // limpa as informações com null
    paciente = Utils.clearObject(paciente);
    return paciente;
  }

  getUser(): unknown {
    if (this.form.get('user').value) {
      return this.form.get('user').value.id;
    }
    return null;
  }

  getSexo(): unknown {
    return this.form.get('sexo').value;
  }

  getDataNascimento(): unknown {
    return this.form.get('data_nascimento').value;
  }

  onFocus(event, input?: boolean) {
    if (input !== undefined && input !== null)
      if (document.activeElement.id === this.ID_FIELD_DTNASC) {
        this.inputElementDtNasc =
          event?.target?.id === document.activeElement.id;
      } else {
        this.inputElementDtNasc = false;
      }
    if (event) {
      this.focusElement = event.target?.id;
    } else {
      this.focusElement = null;
    }
  }

  markAllControlsAsDirty() {
    Object.keys(this.form.controls).forEach(controlKey => {
      controlKey = String(controlKey);
      const control = this.form.get(controlKey);
      if (!control || control.valid) return;
      control.markAsDirty();
    });
  }

  displayError(err: HttpErrorResponse, showDefaultMsg = false) {
    const errorMsg = new ErroMessages({
      route: this.router.url,
      router: this.router,
      matSnackBar: this.matSnackBar,
      httpError: err,
      showDefaultMsg: showDefaultMsg,
      loginService: null,
      tokenService: this.tokenService,
      dialogComponent: ConfirmDialogComponent,
      matDialog: this.matDialog,
      dialogData: {
        config: {
          data: {
            title: 'Aviso - Erro ao realizar operação',
            bodyMessage: err.error,
            icon: 'pi pi-fw pi-info-circle text-orange-500 mb-2 mr-2',
            singleBtn: true
          } as ConfirmDialogData,
          disableClose: true,
          width: '500px'
        } as MatDialogConfig<any>
      }
    } as IErroMessages);
    errorMsg.showMessageObject(err.error);
    // errorMsg.checkError();
  }
}
